import { Device, InstalledApp } from "@/types/device";
import { Application } from "@/types/application";

export interface AppVersionData {
  name: string;
  versions: Array<{
    version: string;
    count: number;
    isLatest: boolean;
  }>;
  totalDevices: number;
}

export interface AppCountData {
  platform: string;
  managed: number;
  unmanaged: number;
  total: number;
}

export interface PlatformAppData {
  platform: string;
  count: number;
  color: string;
}

export interface AppCategoryData {
  category: string;
  count: number;
  color: string;
}

export interface AppActivityData {
  date: string;
  installations: number;
  removals: number;
  updates: number;
}

// Aggregate app versions across all devices
export const aggregateAppVersions = (devices: Device[], applications: Application[]): AppVersionData[] => {
  const appVersionMap = new Map<string, Map<string, number>>();
  
  // Count versions for each app across all devices
  devices.forEach(device => {
    device.installed_apps?.forEach(app => {
      if (!appVersionMap.has(app.name)) {
        appVersionMap.set(app.name, new Map());
      }
      const versionMap = appVersionMap.get(app.name)!;
      const version = app.version || "Unknown";
      versionMap.set(version, (versionMap.get(version) || 0) + 1);
    });
  });

  // Convert to structured data with latest version detection
  return Array.from(appVersionMap.entries())
    .map(([appName, versionMap]) => {
      const app = applications.find(a => a.name === appName);
      const latestVersion = app?.version || "Unknown";
      
      const versions = Array.from(versionMap.entries())
        .map(([version, count]) => ({
          version,
          count,
          isLatest: version === latestVersion
        }))
        .sort((a, b) => b.count - a.count);

      return {
        name: appName,
        versions,
        totalDevices: versions.reduce((sum, v) => sum + v.count, 0)
      };
    })
    .sort((a, b) => b.totalDevices - a.totalDevices)
    .slice(0, 10); // Top 10 apps
};

// Get app count by platform
export const getAppsByPlatform = (devices: Device[], applications: Application[]): AppCountData[] => {
  const platformMap = new Map<string, { managed: Set<string>, unmanaged: Set<string> }>();
  
  devices.forEach(device => {
    const platform = device.os_type;
    if (!platformMap.has(platform)) {
      platformMap.set(platform, { managed: new Set(), unmanaged: new Set() });
    }
    
    const platformData = platformMap.get(platform)!;
    
    device.installed_apps?.forEach(app => {
      const isManaged = applications.some(a => a.name === app.name);
      if (isManaged) {
        platformData.managed.add(app.name);
      } else {
        platformData.unmanaged.add(app.name);
      }
    });
  });

  return Array.from(platformMap.entries()).map(([platform, data]) => ({
    platform,
    managed: data.managed.size,
    unmanaged: data.unmanaged.size,
    total: data.managed.size + data.unmanaged.size
  }));
};

// Get app distribution by platform for pie chart
export const getPlatformAppDistribution = (applications: Application[]): PlatformAppData[] => {
  const platformCounts = applications.reduce((acc, app) => {
    acc[app.platform] = (acc[app.platform] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return Object.entries(platformCounts)
    .map(([platform, count]) => ({
      platform: platform.charAt(0).toUpperCase() + platform.slice(1),
      count,
      color: ""
    }))
    .sort((a, b) => b.count - a.count)
    .map((item, index) => ({
      ...item,
      color: CHART_COLORS[index % CHART_COLORS.length]
    }));
};

// Get app distribution by category
export const getAppsByCategory = (applications: Application[]): AppCategoryData[] => {
  const categoryCounts = applications.reduce((acc, app) => {
    const category = app.categories?.[0] || "Other";
    acc[category] = (acc[category] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return Object.entries(categoryCounts)
    .map(([category, count]) => ({
      category,
      count,
      color: ""
    }))
    .sort((a, b) => b.count - a.count)
    .map((item, index) => ({
      ...item,
      color: CHART_COLORS[index % CHART_COLORS.length]
    }));
};

// Generate mock app activity data
export const getRecentAppActivity = (days: number = 30): AppActivityData[] => {
  const data: AppActivityData[] = [];
  const now = new Date();
  
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(now);
    date.setDate(date.getDate() - i);
    
    // Generate realistic activity patterns
    const dayOfWeek = date.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    
    const baseActivity = isWeekend ? 2 : 8;
    const variance = Math.random() * 0.5 + 0.75; // 75% to 125% of base
    
    data.push({
      date: date.toISOString().split('T')[0],
      installations: Math.floor(baseActivity * variance * Math.random() * 2),
      removals: Math.floor(baseActivity * variance * Math.random() * 0.3),
      updates: Math.floor(baseActivity * variance * Math.random() * 1.5)
    });
  }
  
  return data;
};

// Calculate overall app deployment statistics
// Expanded color palette for better differentiation
const CHART_COLORS = [
  "hsl(220 70% 50%)",  // Blue
  "hsl(160 60% 45%)",  // Teal
  "hsl(30 80% 55%)",   // Orange
  "hsl(280 65% 60%)",  // Purple
  "hsl(120 60% 50%)",  // Green
  "hsl(0 70% 50%)",    // Red
  "hsl(200 80% 50%)",  // Cyan
  "hsl(45 90% 55%)",   // Yellow
  "hsl(300 70% 55%)",  // Magenta
  "hsl(180 50% 40%)",  // Dark Teal
  "hsl(250 60% 55%)",  // Indigo
  "hsl(15 85% 50%)",   // Coral
  "hsl(70 60% 50%)",   // Lime
  "hsl(340 75% 55%)",  // Pink
  "hsl(190 70% 45%)",  // Aqua
  "hsl(25 70% 55%)",   // Rust
];

// Function to get consistent color for an app name
export const getAppColor = (appName: string, index: number): string => {
  // Use app name hash for consistency across charts
  let hash = 0;
  for (let i = 0; i < appName.length; i++) {
    const char = appName.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  const colorIndex = Math.abs(hash) % CHART_COLORS.length;
  return CHART_COLORS[colorIndex];
};

export const calculateAppDeploymentStats = (devices: Device[], applications: Application[]) => {
  const totalManagedApps = applications.length;
  const totalDevices = devices.length;
  
  // Calculate average apps per device
  const totalInstalledApps = devices.reduce((sum, device) => 
    sum + (device.installed_apps?.length || 0), 0
  );
  const avgAppsPerDevice = totalDevices > 0 ? totalInstalledApps / totalDevices : 0;
  
  // Calculate compliance rate (devices with at least one managed app)
  const devicesWithManagedApps = devices.filter(device =>
    device.installed_apps?.some(app =>
      applications.some(managedApp => managedApp.name === app.name)
    )
  ).length;
  const complianceRate = totalDevices > 0 ? (devicesWithManagedApps / totalDevices) * 100 : 0;
  
  return {
    totalManagedApps,
    totalDevices,
    avgAppsPerDevice: Math.round(avgAppsPerDevice * 10) / 10,
    complianceRate: Math.round(complianceRate)
  };
};